import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getCustomers } from 'app/entities/customer/customer.reducer';
import { getEntities as getContacts } from 'app/entities/contact/contact.reducer';
import { DocumentType } from 'app/shared/model/enumerations/document-type.model';
import { SigningSessionStatus } from 'app/shared/model/enumerations/signing-session-status.model';
import { createEntity, getEntity, reset, updateEntity } from './signing-session.reducer';

export const SigningSessionUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const customers = useAppSelector(state => state.customer.entities);
  const contacts = useAppSelector(state => state.contact.entities);
  const signingSessionEntity = useAppSelector(state => state.signingSession.entity);
  const loading = useAppSelector(state => state.signingSession.loading);
  const updating = useAppSelector(state => state.signingSession.updating);
  const updateSuccess = useAppSelector(state => state.signingSession.updateSuccess);
  const documentTypeValues = Object.keys(DocumentType);
  const signingSessionStatusValues = Object.keys(SigningSessionStatus);

  const handleClose = () => {
    navigate('/signing-session');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getCustomers({}));
    dispatch(getContacts({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.documentReferenceId !== undefined && typeof values.documentReferenceId !== 'number') {
      values.documentReferenceId = Number(values.documentReferenceId);
    }
    values.createdAt = convertDateTimeToServer(values.createdAt);
    values.updatedAt = convertDateTimeToServer(values.updatedAt);

    const entity = {
      ...signingSessionEntity,
      ...values,
      customer: customers.find(it => it.id.toString() === values.customer?.toString()),
      contact: contacts.find(it => it.id.toString() === values.contact?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdAt: displayDefaultDateTime(),
          updatedAt: displayDefaultDateTime(),
        }
      : {
          documentType: 'CONTRACT',
          status: 'DRAFT',
          ...signingSessionEntity,
          createdAt: convertDateTimeFromServer(signingSessionEntity.createdAt),
          updatedAt: convertDateTimeFromServer(signingSessionEntity.updatedAt),
          customer: signingSessionEntity?.customer?.id,
          contact: signingSessionEntity?.contact?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.signingSession.home.createOrEditLabel" data-cy="SigningSessionCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.signingSession.home.createOrEditLabel">
              Create or edit a SigningSession
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="signing-session-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.uuid')}
                id="signing-session-uuid"
                name="uuid"
                data-cy="uuid"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.documentType')}
                id="signing-session-documentType"
                name="documentType"
                data-cy="documentType"
                type="select"
              >
                {documentTypeValues.map(documentType => (
                  <option value={documentType} key={documentType}>
                    {translate(`crawlerBackendApp.DocumentType.${documentType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.documentReferenceId')}
                id="signing-session-documentReferenceId"
                name="documentReferenceId"
                data-cy="documentReferenceId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.documentPath')}
                id="signing-session-documentPath"
                name="documentPath"
                data-cy="documentPath"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.signedDocumentPath')}
                id="signing-session-signedDocumentPath"
                name="signedDocumentPath"
                data-cy="signedDocumentPath"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.documentHash')}
                id="signing-session-documentHash"
                name="documentHash"
                data-cy="documentHash"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.status')}
                id="signing-session-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {signingSessionStatusValues.map(signingSessionStatus => (
                  <option value={signingSessionStatus} key={signingSessionStatus}>
                    {translate(`crawlerBackendApp.SigningSessionStatus.${signingSessionStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.createdAt')}
                id="signing-session-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.signingSession.updatedAt')}
                id="signing-session-updatedAt"
                name="updatedAt"
                data-cy="updatedAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="signing-session-customer"
                name="customer"
                data-cy="customer"
                label={translate('crawlerBackendApp.signingSession.customer')}
                type="select"
              >
                <option value="" key="0" />
                {customers
                  ? customers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="signing-session-contact"
                name="contact"
                data-cy="contact"
                label={translate('crawlerBackendApp.signingSession.contact')}
                type="select"
              >
                <option value="" key="0" />
                {contacts
                  ? contacts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/signing-session"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default SigningSessionUpdate;
